import { PlayerInfoSection } from '@/components/common'
import { ExpandMore } from '@/components/icons'
import { useAppSelector } from '@/hooks/useRedux'
import { userSelector } from '@/pages/user/selectors'
import { BracketPayment } from '@/types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  bracketPayment: BracketPayment
  currency?: string
}

export const BracketPaymentItem = ({ bracketPayment, currency }: Props) => {
  const { t } = useTranslation()

  const { user } = useAppSelector(userSelector)

  const [isExpand, setIsExpand] = useState(false)

  const totalBracketAmount = bracketPayment?.brackets.reduce(
    (sum, bracket) => sum + bracket?.paymentDetails?.amountPaid,
    0
  )

  const handlePaymentStatus = (paidAmount: any, refundAmount: any, paymentStatus: any) => {
    if (paymentStatus === 'PENDING') {
      return t('event.paymentPending')
    }
    if (refundAmount === paidAmount && refundAmount && paidAmount && paymentStatus === 'COMPLETE') {
      return t('event.fullyRefunded')
    }
    if (refundAmount < paidAmount && refundAmount && paymentStatus === 'COMPLETE') {
      return t('event.partiallyRefunded')
    }
    if (refundAmount === 0 && paymentStatus === 'COMPLETE') {
      return t('event.received')
    }
    return t('event.amountReceived')
  }

  return (
    <div className="flex flex-col bg-white rounded-2xl p-4 pb-8 cursor-pointer relative">
      <div className="flex justify-between">
        <PlayerInfoSection player={bracketPayment} />
        <div className="flex flex-col gap-y-1 pr-32">
          <span className="text-navy-900 font-semibold text-base">{t('event.totalAmountPaid')}</span>
          <span className="text-navy-900 text-xs">{`(Including Event Fee : ${currency}${bracketPayment?.brackets?.[0]?.paymentDetails?.eventFee})`}</span>
          {bracketPayment?.eventRefundedAmount > 0 ? (
            <span className="text-navy-900 text-xs">{`(Refunded Event Fee : ${currency}${bracketPayment?.eventRefundedAmount})`}</span>
          ) : null}
          <span className="text-navy-900 font-semibold text-base">
            {currency}
            <span>{user?.id !== bracketPayment?.id ? totalBracketAmount + bracketPayment?.eventFee : 0}</span>
          </span>
        </div>
      </div>
      <button
        className="absolute bottom-3 right-4 text-primary-500 text-sm font-semibold flex items-center justify-center"
        onClick={(e) => {
          e.stopPropagation()
          setIsExpand(!isExpand)
        }}
      >
        {t('event.showDetail')}
        <ExpandMore className={`transition-all duration-500 ${isExpand ? 'rotate-180' : 'rotate-0'}`} />
      </button>
      <div
        className={`relative overflow-x-auto border rounded transition-all duration-500 ease-in-out ${
          isExpand ? 'max-h-60 my-4' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <table className="w-full text-xs">
          <thead className="text-xs bg-gray sticky top-0 border-b">
            <tr className="text-gray-400">
              <th scope="col" className="py-3 whitespace-nowrap">
                <p className="border-r-[2px] px-6">{t('event.bracketName')}</p>
              </th>
              <th scope="col" className="py-3 whitespace-nowrap">
                <p className="border-r-[2px] px-6">{t('event.playerStatus')}</p>
              </th>
              <th scope="col" className="py-3 whitespace-nowrap">
                <p className="border-r-[2px] px-6">{t('event.paymentStatus')}</p>
              </th>
              <th scope="col" className="py-3 whitespace-nowrap">
                <p className="border-r-[2px] px-6">{t('event.amountPaid')}</p>
              </th>
              <th scope="col" className="py-3 whitespace-nowrap">
                <p className="px-6">{t('event.amountRefunded')}</p>
              </th>
            </tr>
          </thead>
          <tbody>
            {bracketPayment?.brackets?.map((item) => (
              <tr className="bg-white text-gray-500 font-normal hover:bg-slate-100" key={item.bracketId}>
                <td scope="row" className="px-6 py-4 whitespace-nowrap text-center">
                  <p>{item.name}</p>
                </td>
                <td scope="row" className="px-6 py-4 whitespace-nowrap text-center">
                  <p>
                    {item?.paymentDetails?.playerStatus === 'ACTIVE' &&
                    item?.paymentDetails?.isRegistered &&
                    !item?.paymentDetails?.isWaitListed
                      ? 'Registered'
                      : item?.paymentDetails?.playerStatus === 'FORFEITED' && item?.paymentDetails?.isRegistered
                        ? 'Withdrawn'
                        : item?.paymentDetails?.isWaitListed
                          ? 'Waitlisted'
                          : null}
                  </p>
                </td>
                <td scope="row" className="px-6 py-4 whitespace-nowrap text-center">
                  <p>
                    {handlePaymentStatus(
                      item?.paymentDetails?.amountPaid,
                      item?.paymentDetails?.refundedAmount,
                      item?.paymentDetails?.paymentStatus
                    )}
                  </p>
                </td>
                <td scope="row" className="px-6 py-4 whitespace-nowrap text-center">
                  <p>{`${currency} ${item?.paymentDetails?.amountPaid}`}</p>
                </td>
                <td scope="row" className="px-6 py-4 whitespace-nowrap text-center">
                  <p>{`${currency} ${item?.paymentDetails?.refundedAmount}`}</p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
